import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Home, TrendingDown, Shield, Clock } from 'lucide-react';

export default function LandingPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      if (user.role === 'landlord') navigate('/landlord/dashboard');
      else if (user.role === 'tenant') navigate('/tenant/dashboard');
      else if (user.role === 'admin') navigate('/admin/dashboard');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Glass Header */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50 bg-white/70 border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Home className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
          </div>
          <Button
            data-testid="header-get-started-btn"
            onClick={handleGetStarted}
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-4 sm:px-6 btn-hover"
          >
            {user ? 'Dashboard' : 'Get Started'}
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-14 sm:pb-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-foreground leading-tight">
                Find Your Perfect Home at
                <span className="block text-accent mt-2">Discounted Rates</span>
              </h1>
              <p className="mt-5 sm:mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
                Connect with landlords offering exclusive discounts on quality apartments and homes. Save more on longer rentals and seasonal deals.
              </p>
              <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4">
                <Button
                  data-testid="hero-browse-properties-btn"
                  onClick={() => navigate(user?.role === 'tenant' ? '/tenant/dashboard' : '/auth')}
                  size="lg"
                  className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base btn-hover"
                >
                  Browse Properties
                </Button>
                <Button
                  data-testid="hero-list-property-btn"
                  onClick={() => navigate(user?.role === 'landlord' ? '/landlord/dashboard' : '/auth')}
                  size="lg"
                  variant="outline"
                  className="rounded-full border-2 px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base btn-hover"
                >
                  List Your Property
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1738168246881-40f35f8aba0a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3ODQzNDgwNzJ8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern apartment interior"
                className="rounded-2xl shadow-2xl w-full h-64 sm:h-80 lg:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-12 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-medium tracking-tight">Why Choose PrimePocket?</h2>
            <p className="mt-3 sm:mt-4 text-muted-foreground text-base sm:text-lg">Transparent pricing, exclusive discounts, and quality properties</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-8">
            <div className="bg-card p-6 sm:p-10 rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mb-6">
                <TrendingDown className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-xl font-medium mb-3">Discounted Rates</h3>
              <p className="text-muted-foreground leading-relaxed">
                Landlords offer discounts based on rental duration, seasonal availability, and more. Save up to 30% on select properties.
              </p>
            </div>
            <div className="bg-card p-6 sm:p-10 rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-medium mb-3">Verified Properties</h3>
              <p className="text-muted-foreground leading-relaxed">
                All properties are verified by landlords and reviewed by our admin team to ensure quality and authenticity.
              </p>
            </div>
            <div className="bg-card p-6 sm:p-10 rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-xl font-medium mb-3">Flexible Terms</h3>
              <p className="text-muted-foreground leading-relaxed">
                Choose from short-term or long-term rentals. Enjoy additional discounts for longer commitments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-24 px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-medium tracking-tight mb-4 sm:mb-6">
            Ready to Find Your Next Home?
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mb-8 sm:mb-10">
            Join thousands of tenants and landlords already using PrimePocket
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={handleGetStarted}
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg btn-hover"
          >
            Get Started Today
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10 px-4 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto text-center text-muted-foreground">
          <p>&copy; 2026 PrimePocket. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
